<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div
    :id="postAnchorId"
    class="orangehrm-buzz-wrap"
    :class="{
      'orangehrm-buzz-wrap--pinned': post.pinned,
      'orangehrm-buzz-wrap--highlight': highlighted,
    }"
  >
    <div v-if="post.pinned" class="orangehrm-buzz-pinned-label">
      <oxd-icon name="pin-fill" />
      <oxd-text tag="span">{{ $t('buzz.pinned_post') }}</oxd-text>
    </div>
    <event-countdown-label
      v-if="post.type === 'event' && post.event"
      :event="post.event"
    />
    <oxd-sheet
      :gutters="false"
      type="white"
      class="orangehrm-buzz"
      :class="{'orangehrm-buzz--pinned': post.pinned}"
    >
      <div class="orangehrm-buzz-post">
        <div class="orangehrm-buzz-post-header">
          <div class="orangehrm-buzz-post-header-details">
            <profile-image :employee="post.employee"></profile-image>
            <div class="orangehrm-buzz-post-header-text">
              <buzz-employee-name
                :employee="post.employee"
                tag="p"
                name-class="orangehrm-buzz-post-emp-name"
              />
              <oxd-text tag="p" class="orangehrm-buzz-post-time">
                {{ postDateTime }}
              </oxd-text>
            </div>
          </div>
          <div
            v-if="
              post.permission.canUpdate ||
              post.permission.canDelete ||
              post.permission.canPin ||
              post.permission.canAnnounce
            "
            class="orangehrm-buzz-post-header-config"
          >
            <oxd-dropdown>
              <oxd-icon-button name="three-dots" :with-container="true" />
              <template #content>
                <li
                  v-if="post.permission.canAnnounce"
                  class="orangehrm-buzz-post-header-config-item"
                  @click="$emit('announce', $event)"
                >
                  <oxd-icon name="megaphone" />
                  <oxd-text tag="p">
                    {{ $t('buzz.announce_event') }}
                  </oxd-text>
                </li>
                <li
                  v-if="post.permission.canPin && !post.originalPost"
                  class="orangehrm-buzz-post-header-config-item"
                  @click="$emit('pin', $event)"
                >
                  <oxd-icon :name="post.pinned ? 'pin-fill' : 'pin'" />
                  <oxd-text tag="p">
                    {{
                      post.pinned ? $t('buzz.unpin_post') : $t('buzz.pin_post')
                    }}
                  </oxd-text>
                </li>
                <li
                  v-if="post.permission.canDelete"
                  class="orangehrm-buzz-post-header-config-item"
                  @click="$emit('delete', $event)"
                >
                  <oxd-icon name="trash" />
                  <oxd-text tag="p">
                    {{ $t('buzz.delete_post') }}
                  </oxd-text>
                </li>
                <li
                  v-if="post.permission.canUpdate"
                  class="orangehrm-buzz-post-header-config-item"
                  @click="$emit('edit', $event)"
                >
                  <oxd-icon name="pencil" />
                  <oxd-text tag="p">
                    {{ $t('buzz.edit_post') }}
                  </oxd-text>
                </li>
              </template>
            </oxd-dropdown>
          </div>
        </div>
        <oxd-divider />
      </div>
      <div class="orangehrm-buzz-post-body">
        <slot name="content"></slot>
        <slot name="body"></slot>
      </div>
      <div class="orangehrm-buzz-post-footer">
        <slot name="actionButton"></slot>
        <slot name="postStats"></slot>
      </div>
      <slot name="comments"></slot>
    </oxd-sheet>
  </div>
</template>

<script>
import {computed} from 'vue';
import useLocale from '@/core/util/composable/useLocale';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import ProfileImage from '@/orangehrmBuzzPlugin/components/ProfileImage';
import BuzzEmployeeName from '@/orangehrmBuzzPlugin/components/BuzzEmployeeName';
import EventCountdownLabel from '@/orangehrmBuzzPlugin/components/EventCountdownLabel';
import {OxdDropdownMenu, OxdIcon, OxdSheet} from '@ohrm/oxd';

export default {
  name: 'PostContainer',
  components: {
    'oxd-icon': OxdIcon,
    'oxd-sheet': OxdSheet,
    'oxd-dropdown': OxdDropdownMenu,
    'profile-image': ProfileImage,
    'buzz-employee-name': BuzzEmployeeName,
    'event-countdown-label': EventCountdownLabel,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
    highlighted: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['edit', 'delete', 'pin', 'announce'],

  setup(props) {
    const {locale} = useLocale();
    const {jsDateFormat, jsTimeFormat} = useDateFormat();

    const postDateTime = computed(() => {
      const {createdDate, createdTime} = props.post;

      const utcDate = parseDate(
        `${createdDate} ${createdTime} +00:00`,
        'yyyy-MM-dd HH:mm xxx',
      );

      return formatDate(utcDate, `${jsDateFormat} ${jsTimeFormat}`, {
        locale,
      });
    });

    const postAnchorId = computed(() => `buzz-share-${props.post.id}`);

    return {
      postDateTime,
      postAnchorId,
    };
  },
};
</script>

<style lang="scss" scoped src="./post-container.scss"></style>
